'use strict';

const CONST = require('plugin_frontend_core/constants');
const cookieUtils = require('plugin_frontend_core/utils/cookie-util');
const { exists } = require('plugin_frontend_core/utils/utils_cw');

const selectors = {
    cookieConsent: '.js-cookie-consent',
    cookieConsentOptions: {
        container: '.js-cookie-consent-options',
        item: '.js-cookie-consent-option'
    },
    cookieFeedback: '.js-cookie-feedback',
    cookieNotification: '.js-cookie-notification',
    cookieButton: '.js-cookie-button',
    cookieSettingsToggle: '.js-collapse-toggler-btn',
    cookieModal: '.js-cookie-modal',
    cookieEditButton: '.js-change-cookies'
};

const classes = {
    notificationHidden: 'cookie-notification--hidden',
    overlay: 'has-overlay',
    showModal: 'show-modal',
    notificationModalExpanded: 'cookie-notification-modal--expanded'
};

const attributes = {
    cookieButtonType: 'data-cookie-button-type',
    cookie: {
        category: 'data-cookie-category',
        value: 'data-cookie-value',
        lifetime: 'data-cookie-lifetime'
    }
};

const cookieTypes = {
    useAllCookies: 'useAllCookies',
    useSelectCookies: 'useSelectCookies',
    rejectCookies: 'rejectCookies',
    configureCookies: 'configureCookies'
};

let showModalExpanded = false;

/**
 * Below cookie names are versioned by the date of change is done(not deployed) so that it allows to force customer to re-consent and also see when was the last time there was a change to cookies policy.
 * This MUST be updated in backend JS at 'cartridges/plugin_gdpr/cartridge/scripts/cookieConsentHelper.js'
 * @type {string}
 */
const cookieVersion = '_v3';
const cookiePreferencesSavedCookieName = 'acceptedCookiePolicy' + cookieVersion;
const cookieNames = {
    functional: 'useFunctionalCookies',
    analytical: 'useAnalyticalCookies',
    marketing: 'useMarketingCookies'
};

const pageRootDomain = (function _getRootDomain() {
    const hostnameParts = window.location.hostname.split('.');
    if (hostnameParts.length > 2) {
        return '.' + hostnameParts.slice(-2).join('.');
    }
    return '.' + window.location.hostname;
})();

/**
 * @private hideCookieNotification
 * @description Hides the cookie notification
 */
function hideCookieNotification() {
    const cookieNotification = document.querySelector(selectors.cookieNotification);
    const modal = document.querySelector(selectors.cookieModal);

    if (!exists(cookieNotification)) {
        console.error(`hideCookieNotification: required element(s) missing: ${selectors.cookieNotification}`); // eslint-disable-line no-console
        return;
    }

    // Remove classes associated with showing the cookie modal
    if (modal) {
        modal.classList.remove(classes.showModal);
        document.body.classList.remove(classes.overlay);
    }

    cookieNotification.classList.add(classes.notificationHidden);
}

/**
 * @private hideCookieConsentElementAmongContent
 * @description Response handler for handling the cookie submission
 */
function hideCookieConsentElementAmongContent() {
    const cookieFeedback = document.querySelector(selectors.cookieFeedback);

    if (!exists(cookieFeedback)) {
        console.error(`hideCookieConsentElementAmongContent: required element(s) missing: ${selectors.cookieFeedback}`); // eslint-disable-line no-console
        return;
    }

    cookieFeedback.classList.remove(CONST.classes.dNone);
}

/**
 * @private getCookieAttributes
 * @description Gets all required cookie attributes from the passed cookie parameter
 * @param {Node} cookie - the cookie to get the attributes from
 * @returns {Object} - the cookie object with its attributes
 */
function getCookieAttributes(cookie) {
    const cookieObject = {};

    const category = cookie.getAttribute(attributes.cookie.category);
    const value = cookie.checked;
    const lifetime = cookie.getAttribute(attributes.cookie.lifetime);
    if (!category || !lifetime) {
        console.error(`getCookieAttributes: required attribute(s) missing: ${attributes.cookie.category} || ${attributes.cookie.value} || ${attributes.cookie.lifetime}`); // eslint-disable-line no-console
        return null;
    }

    cookieObject.value = value;
    cookieObject.lifetime = lifetime;

    // Set cookie name based on the cookie category
    switch (category) {
        case 'functionalCookies':
            cookieObject.name = cookieNames.functional;
            break;
        case 'analyticalCookies':
            cookieObject.name = cookieNames.analytical;
            break;
        case 'marketingCookies':
            cookieObject.name = cookieNames.marketing;
            break;
        default:
            break;
    }

    return cookieObject;
}

/**
 * @private processUserCookieConsentPreferences
 * @description
 *      Gets all the cookies and, based on their values, pushes them to an array
 *      This array will then be used to set cookies based on their value
 * @param {EventTarget} target - the target to get the selected cookies from
 * @param {string} clickedButtonType - the type of the target
 */
function processUserCookieConsentPreferences(target, clickedButtonType) {
    if (clickedButtonType === cookieTypes.configureCookies) {
        const modal = document.querySelector(selectors.cookieModal);
        modal.classList.add(classes.notificationModalExpanded);
        return;
    }

    // Object to collect current and new state of consents
    const cookieStates = {
        currentStates: {},
        newStates: {}
    };

    // Collect current state of the cookies
    cookieStates.currentStates[cookieNames.functional] = cookieUtils.getCookie(cookieNames.functional) === 'true';
    cookieStates.currentStates[cookieNames.analytical] = cookieUtils.getCookie(cookieNames.analytical) === 'true';
    cookieStates.currentStates[cookieNames.marketing] = cookieUtils.getCookie(cookieNames.marketing) === 'true';

    const cookieElement = target.closest(selectors.cookieNotification) || target.closest(selectors.cookieConsent);
    if (!cookieElement) {
        console.error(`processUserCookieConsentPreferences: required element(s) missing: ${selectors.cookieNotification} || ${selectors.cookieConsent}`); // eslint-disable-line no-console
        return;
    }

    const cookieOptionsContainer = cookieElement.querySelector(selectors.cookieConsentOptions.container);
    if (!exists(cookieOptionsContainer)) {
        console.error(`processUserCookieConsentPreferences: required element(s) missing: ${selectors.cookieConsentOptions.container}`); // eslint-disable-line no-console
        return;
    }

    const cookieOptions = cookieOptionsContainer.querySelectorAll(selectors.cookieConsentOptions.item);
    if (!exists(cookieOptions)) {
        console.error(`processUserCookieConsentPreferences: required element(s) missing: ${selectors.cookieConsentOptions.item}`); // eslint-disable-line no-console
        return;
    }

    for (const cookieOption of cookieOptions) {
        // Check/uncheck the cookie if user clicks on 'accept all'/'reject all' button
        if (clickedButtonType === cookieTypes.useAllCookies) {
            cookieOption.checked = true;
        } else if (clickedButtonType === cookieTypes.rejectCookies) {
            cookieOption.checked = cookieOption.dataset.required === 'true';
        }

        const cookie = getCookieAttributes(cookieOption);
        if (!cookie) {
            console.error('processUserCookieConsentPreferences: required element(s) missing; refer to getCookieAttributes function'); // eslint-disable-line no-console
            return;
        }

        // Set all cookies based on the selected cookieOptions individually
        cookieUtils.setCookie(cookie.name, cookie.value, cookie.lifetime);

        // Store new state of the cookie
        cookieStates.newStates[cookie.name] = cookie.value;
    }

    // Set cookie to state that user saved their cookie preferences
    cookieUtils.setCookie(cookiePreferencesSavedCookieName, true, 365);

    // Hide the cookie notification if the target is inside the cookie notification
    if (cookieElement.classList.contains(selectors.cookieNotification.slice(1))) {
        hideCookieNotification();
    }

    // Handle cookie submission if the target is inside the cookie consent
    if (cookieElement.classList.contains(selectors.cookieConsent.slice(1))) {
        hideCookieConsentElementAmongContent();
    }

    // Fire custom event 'cookiePreferencesSaved' with given consents info so that followup can be done
    const cookiePreferencesSavedEvent = new CustomEvent(
        'cookiePreferencesSaved',
        {
            detail: {
                useFunctionalCookies: {
                    current: cookieStates.currentStates[cookieNames.functional],
                    new: cookieStates.newStates[cookieNames.functional]
                },
                useAnalyticalCookies: {
                    current: cookieStates.currentStates[cookieNames.analytical],
                    new: cookieStates.newStates[cookieNames.analytical]
                },
                useMarketingCookies: {
                    current: cookieStates.currentStates[cookieNames.marketing],
                    new: cookieStates.newStates[cookieNames.marketing]
                }
            }
        });

    document.dispatchEvent(cookiePreferencesSavedEvent);
}

/**
 * @private onCookiePreferenceClick
 * @description Handles click event on cookie preference buttons
 * @param {Event} event - the event
 */
function onCookiePreferenceClick(event) {
    const { currentTarget } = event;

    const targetType = currentTarget.getAttribute(attributes.cookieButtonType);
    if (!targetType) {
        console.warn(`setCookie: required attribute(s) missing: ${attributes.cookieButtonType}`); // eslint-disable-line no-console
        return;
    }

    processUserCookieConsentPreferences(currentTarget, targetType);
}

/**
 * @private showModal
 * @description handles setting classes for the modal
 */
function showModal() {
    const modal = document.querySelector(selectors.cookieModal);
    const cookieNotification = document.querySelector(selectors.cookieNotification);

    if (cookieNotification) {
        cookieNotification.classList.remove(classes.notificationHidden);
    }

    if (modal) {
        if (showModalExpanded) {
            modal.classList.add(classes.notificationModalExpanded);
        }

        modal.classList.add(classes.showModal);
        document.body.classList.add(classes.overlay);
    }
}

/**
 * @private handleDropDownActiveState
 * @description manipulates the dropdown toggle button for each cookie on click
 */
function handleDropDownActiveState() {
    const btnDropdowns = document.querySelectorAll(selectors.cookieSettingsToggle);

    if (btnDropdowns && btnDropdowns.length !== -1) {
        Array.from(btnDropdowns).forEach(btn => {
            btn.addEventListener(CONST.events.click, () => {
                btn.classList.toggle(CONST.classes.active);
            });
        });
    }
}

/**
 * @private getCookieModal
 * @description gets the cookie modal
 */
function getCookieModal() {
    const endpoint = 'sfUrls' in window && window.sfUrls.cookies.getSettingsModal;
    if (endpoint) {
        $.ajax({
            url: `${endpoint}?force=true`,
            method: 'GET',
            success: function (modalHtml) {
                $('body').append(modalHtml);
                init();
            }
        });
    }
}

/**
 * @private openCookieSettings
 * @description gets and opens the modal with the cookiesettings
 */
function openCookieSettings(e) {
    e.preventDefault();
    showModalExpanded = true;
    const cookieNotification = document.querySelector(selectors.cookieNotification);
    if (!exists(cookieNotification)) {
        getCookieModal();
    } else {
        showModal();
    }
}

/**
 * Removes GTM and Google Analytics scripts and cookies
 * @private
 */
function _removeAnalyticalScriptsAndCookies() {
    // 1. Remove Google Tag Manager and Analytics <script> tags
    document.querySelectorAll('script[src*="googletagmanager"], script[src*="google-analytics"]').forEach(el => {
        el.remove();
    });

    // 2. Delete cookies
    const analyticalCookies = [
        '_ga', // This cookie identifies unique users over a long period, typically two years
        '_gid', // This cookie is used for short-term user identification and tracks session-level data, expiring after 24 hours
        'lantern', // This cookie is set by GTM script
        '__cq_uuid', // Unique anonymous visitor ID for session and behavioral analysis.
        '__cq_bc', // Used by Salesforce Commerce Cloud to track on-site behaviour for personalised offers.
        '__cq_seg', // Stores which marketing segments a visitor belongs to for targeted content.
        '_hjSession', // HotJar cookies
        'amplitude_id_', // Trustpilot
        'TP.uuid', // Trustpilot
        'ajs_anonymouw_id' // Trustpilot
    ];
    analyticalCookies.forEach(function (cookieName) {
        cookieUtils.getCookiesByPrefix(cookieName).forEach(cookie => {
            const cookieKeyValue = cookie.trim().split('=');
            cookieUtils.deleteCookie(cookieKeyValue[0], pageRootDomain);
        });
    });
}

/**
 * Removes Marketing cookies
 * @private
 */
function _removeMarketingScriptsAndCookies() {
    // Delete cookies
    const marketingCookies = [
        '_pin_unauth', // Pinterest tracking for non-logged-in users.
        '__cq_uuid', // Unique anonymous visitor ID for session and behavioral analysis.
        '__cq_bc', // Used by Salesforce Commerce Cloud to track on-site behaviour for personalised offers.
        '__cq_seg', // Stores which marketing segments a visitor belongs to for targeted content.
        '_gcl_au', // Google marketing cookie used for conversion tracking, storing a browser identifier to link ad clicks to website conversions
        '_fbp', // Facebook Pixel identification for targeted advertising campaigns.
        'test_cookie', // DoubleClick test cookie to check if the browser accepts cookies.
        '__adroll_fpc', // Trustpilot
        '__Secure-1PAPISID', // Google
        '__Secure-1PSID', // Google
        '__Secure-3PAPISID', // Google
        '__Secure-3PSID', // Google
        'APISID', // Google
        'SAPISID', // Google
        'SSID', // Google
        'NID', // Google
        '_derived_epik' // Schuurman
    ];
    marketingCookies.forEach(function (cookieName) {
        const matchedCookies = cookieUtils.getCookiesByPrefix(cookieName);
        matchedCookies.forEach(cookie => {
            const cookieKeyValue = cookie.trim().split('=');
            cookieUtils.deleteCookie(cookieKeyValue[0], pageRootDomain);
        });
    });
}

/**
 * @public init
 * @description Initializes the module
 */
function init() {
    const cookieButtons = document.querySelectorAll(selectors.cookieButton);

    const openCookieSettingsButton = document.querySelector(selectors.cookieEditButton);
    if (exists(openCookieSettingsButton)) {
        openCookieSettingsButton.addEventListener(CONST.events.click, openCookieSettings);
    }

    if (!exists(cookieButtons)) {
        return;
    }

    for (const cookieButton of cookieButtons) {
        cookieButton.addEventListener(CONST.events.click, onCookiePreferenceClick);
    }

    showModal();
    handleDropDownActiveState();

    /**
     * When user interacts with cookie banner and accepts/updates consents
     * Available custom data; event.detail.useFunctionalCookies, event.detail.useMarketingCookies, event.detail.useAnalyticalCookies
     */
    document.addEventListener('cookiePreferencesSaved', function (event) {
        // If Analytical cookies revoked, then remove related scripts, cookies, and re-declare gtag() function to avoid any datalayer submission
        if (!event.detail.useAnalyticalCookies.new) {
            _removeAnalyticalScriptsAndCookies();

            // Disable dataLayer & GA functions to prevent further calls
            window.dataLayer = [];
            window.gtag = function () {
                console.warn('Analytics disabled - gtag call ignored.');
            };
        }

        // If Marketing cookies revoked, then remove related cookies
        if (!event.detail.useMarketingCookies.new) {
            _removeMarketingScriptsAndCookies();
        }

        // Set SFCC internal cookies to avoid custom tracking.
        let trackingAllowed;
        if (!event.detail.useAnalyticalCookies.new || !event.detail.useMarketingCookies.new) {
            cookieUtils.setCookie('dw_dnt', '1');
            cookieUtils.setCookie('__cq_dnt', '1');
            trackingAllowed = false;
        } else {
            cookieUtils.setCookie('dw_dnt', '0');
            cookieUtils.setCookie('__cq_dnt', '0');
            trackingAllowed = true;
        }

        const consentApiEndPoint = 'sfUrls' in window && window.sfUrls.cookies.consentApi;
        if (consentApiEndPoint) {
            $.ajax({
                url: `${consentApiEndPoint}?consent=${trackingAllowed}`,
                method: 'GET',
                success: function () {
                }
            });
        }

        // If user just consented analytical or marketing cookies, and it wasn't consented before, then refresh the page so that GTM script can be injected
        if (event.detail.useAnalyticalCookies.current !== event.detail.useAnalyticalCookies.new
            || event.detail.useMarketingCookies.new && event.detail.useMarketingCookies.current !== event.detail.useMarketingCookies.new) {
            window.location.reload();
        }
    });
}

module.exports = {
    init: init
};
